import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import api from '../services/api.ts';
import FormPage from '../components/ui/FormPage.tsx';
import MoreOptions from '../components/ui/MoreOptions.tsx';

const CreateReceivable: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const backPath = '/financeiro/contas?tab=receber';

  const [customerName, setCustomerName] = useState('');
  const [description, setDescription] = useState('');
  const [amountDisplay, setAmountDisplay] = useState('');
  const [amount, setAmount] = useState(0);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState<'real_estate' | 'vehicle' | 'mutuos' | 'other'>('other');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation(
    async () => {
      await api.post('/financeiro/contas-a-receber', {
        customerName,
        description,
        originalAmount: amount,
        currentAmount: amount,
        interestRate: 0,
        dueDate,
        status: 'active',
        category,
        paymentMethod: paymentMethod || undefined,
        installments: 1,
        currentInstallment: 1,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('receivables');
        queryClient.invalidateQueries('contas-a-receber');
        navigate(backPath);
      },
      onError: () => setErrors({ submit: 'Erro ao criar conta a receber. Tente novamente.' }),
    }
  );

  const validate = () => {
    const next: Record<string, string> = {};
    if (!description.trim()) next.description = 'Informe a descrição da conta.';
    if (!customerName.trim()) next.customerName = 'Informe o cliente.';
    if (!amount || amount <= 0) next.amount = 'Informe um valor maior que zero.';
    if (!dueDate) next.dueDate = 'Informe a data de vencimento.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) mutation.mutate();
  };

  return (
    <FormPage
      backTo={backPath}
      backLabel="Voltar às contas a receber"
      breadcrumb="Financeiro / Contas / A receber / Nova"
      title="Nova conta a receber"
      subtitle="Valor a entrar — ainda não entrou no caixa"
      hint="Campos essenciais primeiro. Use “Mais opções” só se precisar."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(backPath)}>
            Cancelar
          </button>
          <button
            type="submit"
            form="create-receivable-form"
            className="btn btn-primary"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar conta a receber'}
          </button>
        </>
      }
    >
      <form id="create-receivable-form" noValidate onSubmit={onSubmit}>
        {errors.submit && <div className="field-error" style={{ marginBottom: 12 }}>{errors.submit}</div>}
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="description">Descrição *</label>
            <input
              id="description"
              className={`input${errors.description ? ' error' : ''}`}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Aluguel, parcela de venda"
            />
            {errors.description && <div className="field-error">{errors.description}</div>}
          </div>
          <div className="field">
            <label htmlFor="customerName">Cliente *</label>
            <input
              id="customerName"
              className={`input${errors.customerName ? ' error' : ''}`}
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
            />
            {errors.customerName && <div className="field-error">{errors.customerName}</div>}
          </div>
          <div className="field">
            <label htmlFor="amount">Valor *</label>
            <input
              id="amount"
              className={`input${errors.amount ? ' error' : ''}`}
              value={amountDisplay}
              onChange={e => {
                const masked = applyCurrencyMask(e.target.value);
                setAmountDisplay(masked);
                setAmount(parseCurrency(masked));
              }}
              placeholder="R$ 0,00"
            />
            {errors.amount && <div className="field-error">{errors.amount}</div>}
          </div>
          <div className="field">
            <label htmlFor="dueDate">Vencimento *</label>
            <input
              id="dueDate"
              type="date"
              className={`input${errors.dueDate ? ' error' : ''}`}
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
            />
            {errors.dueDate && <div className="field-error">{errors.dueDate}</div>}
          </div>
        </div>
        <MoreOptions>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="category">Categoria</label>
              <select
                id="category"
                className="select"
                value={category}
                onChange={e => setCategory(e.target.value as typeof category)}
              >
                <option value="other">Outros</option>
                <option value="real_estate">Imóvel</option>
                <option value="vehicle">Veículo</option>
                <option value="mutuos">Mútuos</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="paymentMethod">Forma preferida</label>
              <input
                id="paymentMethod"
                className="input"
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                placeholder="PIX, boleto…"
              />
            </div>
          </div>
        </MoreOptions>
      </form>
    </FormPage>
  );
};

export default CreateReceivable;
